/**
 * @fileoverview Provide searchable shared selection over caller-owned options. The workspace owns the chosen value and any data fetching; the control owns transient search and overlay state. Selection emits a value/label envelope, unlike the direct string callback in `AppSelect.js`. `AppIcon.js` supplies common visual signifiers.
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import AppIcon from "./AppIcon";

const baseInputStyle = {
  width: '100%',
  padding: '0.6rem 0.75rem',
  paddingRight: '2rem',
  borderRadius: '6px',
  border: '1px solid var(--surface-border)',
  fontSize: '1rem',
  backgroundColor: 'var(--surface-section)',
  color: 'var(--text-color)',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
  position: 'relative',
  outline: 'none',
  appearance: 'none',
  display: 'flex',
  alignItems: 'center'
};

const borderlessInputStyle = {
  ...baseInputStyle,
  border: 'none',
  borderBottom: '1px solid transparent',
  borderRadius: '0',
  backgroundColor: 'transparent',
  padding: '0.6rem 2rem 0.6rem 0'
};

const chevronStyle = {
  position: 'absolute',
  right: '0.75rem',
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  fontSize: '0.75rem',
  color: 'var(--text-color-secondary, #64748b)'
};

const panelStyle = {
  position: 'fixed',
  backgroundColor: 'var(--surface-card)',
  border: '1px solid var(--surface-border)',
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
  zIndex: 5000,
  maxHeight: '250px',
  overflowY: 'auto',
  padding: '0.25rem 0'
};

const optionStyle = {
  padding: '0.6rem 1rem',
  cursor: 'pointer',
  fontSize: '0.875rem',
  color: 'var(--text-color)',
  whiteSpace: 'nowrap'
};

/**
 * onChange receives {value, label}. optionLabel/optionValue adapt domain records to selection choices without changing their source ownership. The caller remains responsible for validating and persisting the choice.
 */
function AppDropdown({ id, value, options = [], onChange, placeholder, variant, optionLabel, optionValue, disabled, className, autoFocus = false, openOnAutoFocus = false }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 0 });

  const normalizedOptions = options.map(

  (opt) => {
    if (optionLabel && optionValue) {
      return { label: opt[optionLabel], value: opt[optionValue] };
    }
    if (opt.label !== undefined && opt.value !== undefined) {
      return opt;
    }
    if (opt.name !== undefined) {
      return { label: opt.name, value: opt.name };
    }
    return { label: String(opt), value: opt };
  });

  const filtered = search ?
  normalizedOptions.filter((o) => o.label.toLowerCase().includes(search.toLowerCase())) :
  normalizedOptions;

  const selectedLabel = normalizedOptions.find((o) => o.value === value)?.label;

  useEffect(

  () => {
    if (!open) {
      setSearch('');
      setHighlightIndex(-1);
      return;
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setPanelPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setTimeout(() => searchRef.current?.focus(), 0);

    const handleClickOutside =

    (e) => {
      if (panelRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!autoFocus || disabled) return;

    const frame = requestAnimationFrame(() => {
      triggerRef.current?.focus();
      if (openOnAutoFocus) {
        setOpen(true);
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [autoFocus, disabled, openOnAutoFocus]);

  const select =

  (opt) => {
    onChange({ value: opt.value, label: opt.label });
    setOpen(false);
  };

  const handleKeyDown =

  (e) => {
    if (e.key === 'Escape') {setOpen(false);return;}
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0 && filtered[highlightIndex]) {
      e.preventDefault();
      select(filtered[highlightIndex]);
    }
  };

  const isBorderless = variant === 'borderless';
  const inputSt = isBorderless ? borderlessInputStyle : baseInputStyle;

  return (
    <div style={{ position: 'relative' }} className={className || ''}>
            <button
        ref={triggerRef}
        id={id}
        type="button"
        style={{
          ...inputSt,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer'
        }}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        onFocus={isBorderless ? (e) => {e.currentTarget.style.borderBottom = '1px solid var(--primary-color)';} : undefined}
        onBlur={isBorderless ? (e) => {if (!open) e.currentTarget.style.borderBottom = '1px solid transparent';} : undefined}>

                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {selectedLabel || <span style={{ color: 'var(--text-color-secondary, #94a3b8)' }}>{placeholder || ''}</span>}
                </span>
                <span style={chevronStyle}><AppIcon icon="pi pi-chevron-down" /></span>
            </button>

            {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={panelRef}
          style={{ ...panelStyle, top: panelPos.top, left: panelPos.left, minWidth: panelPos.width }}
          onKeyDown={handleKeyDown}>

                    {normalizedOptions.length > 6 &&
          <div style={{ padding: '0.5rem' }}>
                            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => {setSearch(e.target.value);setHighlightIndex(0);}}
              placeholder="Search..."
              style={{
                width: '100%', padding: '0.4rem 0.6rem', borderRadius: '4px',
                border: '1px solid var(--surface-border)', fontSize: '0.875rem',
                backgroundColor: 'var(--surface-section)', color: 'var(--text-color)',
                outline: 'none'
              }}
              onKeyDown={handleKeyDown} />

                        </div>}

                    {filtered.map((opt, i) =>
          <div
            key={i}
            style={{
              ...optionStyle,
              backgroundColor: i === highlightIndex ?
              'var(--surface-hover, var(--surface-ground))' :
              opt.value === value ?
              'var(--surface-100, var(--surface-ground))' :
              'transparent',
              fontWeight: opt.value === value ? 600 : 400
            }}
            onMouseEnter={() => setHighlightIndex(i)}
            onClick={() => select(opt)}>

                            {opt.label}
                        </div>
          )}
                    {filtered.length === 0 &&
          <div style={{ ...optionStyle, color: 'var(--text-color-secondary)' }}>No results</div>}

                </div>,
        document.body
      )}
        </div>);

}

export default AppDropdown;
